import React from 'react';
import { motion } from 'framer-motion';
import { Building2, Users, AlertTriangle, TrendingDown } from 'lucide-react';
import { Department } from '../data/employees';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

interface DepartmentBreakdownProps {
  departments: Department[];
}

const DepartmentBreakdown: React.FC<DepartmentBreakdownProps> = ({ departments }) => {
  const sortedDepts = [...departments].sort((a, b) => b.avgRiskScore - a.avgRiskScore);

  const chartData = departments.map((dept) => ({
    name: dept.name.length > 10 ? dept.name.slice(0, 10) + '...' : dept.name,
    fullName: dept.name,
    highRisk: dept.highRiskCount,
    mediumRisk: dept.mediumRiskCount,
    lowRisk: dept.lowRiskCount,
    avgScore: dept.avgRiskScore,
  }));

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const getScoreColor = (score: number) => {
    if (score >= 50) return '#EF4444';
    if (score >= 30) return '#F59E0B';
    return '#10B981';
  };

  return (
    <motion.div
      style={styles.container}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Summary Header */}
      <motion.div variants={itemVariants} style={styles.summaryHeader}>
        <div style={styles.summaryCard}>
          <Building2 size={24} color="#69277F" />
          <div>
            <span style={styles.summaryValue}>{departments.length}</span>
            <span style={styles.summaryLabel}>Departments</span>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <Users size={24} color="#06BCCA" />
          <div>
            <span style={styles.summaryValue}>
              {departments.reduce((sum, d) => sum + d.headCount, 0)}
            </span>
            <span style={styles.summaryLabel}>Total Employees</span>
          </div>
        </div>
        <div style={styles.summaryCard}>
          <AlertTriangle size={24} color="#EF4444" />
          <div>
            <span style={styles.summaryValue}>
              {departments.reduce((sum, d) => sum + d.highRiskCount, 0)}
            </span>
            <span style={styles.summaryLabel}>High Risk Total</span>
          </div>
        </div>
      </motion.div>

      {/* Engagement Heatmap by Team */}
      <motion.div variants={itemVariants} style={styles.heatmapSection}>
        <h3 style={styles.sectionTitle}>Engagement Heatmap by Team</h3>
        <p style={styles.sectionSubtitle}>Company-wide health map</p>
        <div style={styles.heatmapGrid}>
          {departments.map((dept, index) => {
            const riskLevel = dept.avgRiskScore >= 70 ? 'high' : dept.avgRiskScore >= 50 ? 'medium' : 'low';
            return (
              <div key={index} style={styles.heatmapCell}>
                <div style={styles.heatmapCellHeader}>
                  <span style={styles.heatmapCellName}>{dept.name}</span>
                  <span style={{
                    ...styles.heatmapCellBadge,
                    background: riskLevel === 'high' ? '#FEE2E2' : riskLevel === 'medium' ? '#FEF3C7' : '#D1FAE5',
                    color: riskLevel === 'high' ? '#DC2626' : riskLevel === 'medium' ? '#D97706' : '#059669',
                  }}>
                    {dept.avgRiskScore}
                  </span>
                </div>
                <div style={styles.heatmapCellMetrics}>
                  <div style={styles.heatmapMetric}>
                    <span style={styles.heatmapMetricLabel}>Red Flags:</span>
                    <span style={styles.heatmapMetricValue}>{dept.highRiskCount}</span>
                  </div>
                  <div style={styles.heatmapMetric}>
                    <span style={styles.heatmapMetricLabel}>Trend:</span>
                    <TrendingDown size={14} color={riskLevel === 'high' ? '#EF4444' : '#10B981'} />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* Chart Section */}
      <motion.div variants={itemVariants} style={styles.chartSection}>
        <h3 style={styles.sectionTitle}>Risk Distribution by Department</h3>
        <div style={styles.chartWrapper}>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData} layout="vertical" barCategoryGap="20%">
              <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" horizontal={true} vertical={false} />
              <XAxis type="number" tick={{ fontSize: 12 }} stroke="#9CA3AF" />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 12 }} stroke="#9CA3AF" width={100} />
              <Tooltip
                contentStyle={{
                  borderRadius: '12px',
                  border: 'none',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
                }}
                formatter={(value: number, name: string) => {
                  const labels: Record<string, string> = {
                    highRisk: 'High Risk',
                    mediumRisk: 'Medium Risk',
                    lowRisk: 'Low Risk',
                  };
                  return [value, labels[name] || name];
                }}
              />
              <Bar dataKey="highRisk" stackId="a" fill="#EF4444" radius={[0, 0, 0, 0]} />
              <Bar dataKey="mediumRisk" stackId="a" fill="#F59E0B" radius={[0, 0, 0, 0]} />
              <Bar dataKey="lowRisk" stackId="a" fill="#10B981" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div style={styles.chartLegend}>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#EF4444' }} />
            <span>High Risk</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#F59E0B' }} />
            <span>Medium Risk</span>
          </div>
          <div style={styles.legendItem}>
            <div style={{ ...styles.legendDot, background: '#10B981' }} />
            <span>Low Risk</span>
          </div>
        </div>
      </motion.div>

      {/* Department Cards */}
      <motion.div variants={itemVariants}>
        <h3 style={styles.sectionTitle}>Department Details</h3>
        <div style={styles.deptGrid}>
          {sortedDepts.map((dept, index) => (
            <motion.div
              key={dept.name}
              style={styles.deptCard}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              whileHover={{ y: -4, boxShadow: '0 10px 40px rgba(105, 39, 127, 0.1)' }}
            >
              <div style={styles.deptHeader}>
                <div style={styles.deptIcon}>
                  <Building2 size={18} color="#69277F" />
                </div>
                <div style={styles.deptInfo}>
                  <h4 style={styles.deptName}>{dept.name}</h4>
                  <span style={styles.deptHeadcount}>{dept.headCount} employees</span>
                </div>
                <div
                  style={{
                    ...styles.avgScoreBadge,
                    background: `${getScoreColor(dept.avgRiskScore)}15`,
                    color: getScoreColor(dept.avgRiskScore),
                  }}
                >
                  {dept.avgRiskScore}
                </div>
              </div>

              <div style={styles.riskBreakdown}>
                <div style={styles.riskRow}>
                  <span style={styles.riskLabel}>
                    <span style={{ ...styles.riskDot, background: '#EF4444' }} />
                    High Risk
                  </span>
                  <span style={styles.riskCount}>{dept.highRiskCount}</span>
                  <div style={styles.riskBarWrapper}>
                    <motion.div
                      style={{
                        ...styles.riskBarFill,
                        background: '#EF4444',
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${(dept.highRiskCount / dept.headCount) * 100}%` }}
                      transition={{ delay: 0.3 }}
                    />
                  </div>
                </div>
                <div style={styles.riskRow}>
                  <span style={styles.riskLabel}>
                    <span style={{ ...styles.riskDot, background: '#F59E0B' }} />
                    Medium
                  </span>
                  <span style={styles.riskCount}>{dept.mediumRiskCount}</span>
                  <div style={styles.riskBarWrapper}>
                    <motion.div
                      style={{
                        ...styles.riskBarFill,
                        background: '#F59E0B',
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${(dept.mediumRiskCount / dept.headCount) * 100}%` }}
                      transition={{ delay: 0.4 }}
                    />
                  </div>
                </div>
                <div style={styles.riskRow}>
                  <span style={styles.riskLabel}>
                    <span style={{ ...styles.riskDot, background: '#10B981' }} />
                    Low Risk
                  </span>
                  <span style={styles.riskCount}>{dept.lowRiskCount}</span>
                  <div style={styles.riskBarWrapper}>
                    <motion.div
                      style={{
                        ...styles.riskBarFill,
                        background: '#10B981',
                      }}
                      initial={{ width: 0 }}
                      animate={{ width: `${(dept.lowRiskCount / dept.headCount) * 100}%` }}
                      transition={{ delay: 0.5 }}
                    />
                  </div>
                </div>
              </div>

              {dept.highRiskCount > dept.headCount * 0.2 && (
                <div style={styles.alertBanner}>
                  <AlertTriangle size={14} />
                  <span>High risk concentration detected</span>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </motion.div>
    </motion.div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  summaryHeader: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
  },
  summaryCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  summaryValue: {
    display: 'block',
    fontSize: '28px',
    fontWeight: 700,
    color: '#1F2937',
    fontFamily: 'var(--font-heading)',
  },
  summaryLabel: {
    display: 'block',
    fontSize: '13px',
    color: '#6B7280',
  },
  chartSection: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '20px',
  },
  sectionSubtitle: {
    fontSize: '13px',
    color: '#9CA3AF',
    marginBottom: '20px',
  },
  heatmapSection: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    marginBottom: '24px',
  },
  heatmapGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '16px',
  },
  heatmapCell: {
    padding: '16px',
    background: '#F9FAFB',
    borderRadius: '12px',
    border: '1px solid #E5E7EB',
  },
  heatmapCellHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  heatmapCellName: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
  },
  heatmapCellBadge: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 600,
  },
  heatmapCellMetrics: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  heatmapMetric: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontSize: '12px',
  },
  heatmapMetricLabel: {
    color: '#6B7280',
  },
  heatmapMetricValue: {
    fontWeight: 600,
    color: '#1F2937',
  },
  chartWrapper: {
    width: '100%',
  },
  chartLegend: {
    display: 'flex',
    justifyContent: 'center',
    gap: '24px',
    marginTop: '16px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#6B7280',
  },
  legendDot: {
    width: '12px',
    height: '12px',
    borderRadius: '4px',
  },
  deptGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: '20px',
  },
  deptCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
    transition: 'all 0.3s ease',
  },
  deptHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
  },
  deptIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '10px',
    background: 'rgba(105, 39, 127, 0.1)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  deptInfo: {
    flex: 1,
  },
  deptName: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1F2937',
    margin: 0,
  },
  deptHeadcount: {
    fontSize: '13px',
    color: '#6B7280',
  },
  avgScoreBadge: {
    padding: '6px 12px',
    borderRadius: '20px',
    fontSize: '14px',
    fontWeight: 700,
  },
  riskBreakdown: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  riskRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  riskLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '13px',
    color: '#6B7280',
    minWidth: '90px',
  },
  riskDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  riskCount: {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1F2937',
    minWidth: '24px',
    textAlign: 'right',
  },
  riskBarWrapper: {
    flex: 1,
    height: '6px',
    background: '#F3F4F6',
    borderRadius: '3px',
    overflow: 'hidden',
  },
  riskBarFill: {
    height: '100%',
    borderRadius: '3px',
  },
  alertBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '16px',
    padding: '10px 14px',
    background: 'rgba(245, 158, 11, 0.1)',
    borderRadius: '10px',
    fontSize: '13px',
    color: '#D97706',
    fontWeight: 500,
  },
};

export default DepartmentBreakdown;

